import { ApiQueries, useSettings, ShowTabsEnum, Features, ClassDetails, ClassAttendance, ClassLessons, ClassOutline, AssessmentSummary, AssessmentTaskList, AssessmentSchedule } from "daymap-react";
import { PageContainer } from "../../../../components/layout";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Stack, Tabs, Tab, Box, Menu, MenuItem, Switch, Typography, CircularProgress } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PageHeader from '../../../../containers/pageheader/pageheader';
import { useDiaryItemClick } from "../../../../components/diary";
import { useState } from "react";


export function ClassDetail() {
    const navigate = useNavigate();
    const { classId: classIdParam, studentId: studentIdParam, year: yearParam } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { handleItemClick, modalElement } = useDiaryItemClick();
    
    const classId = Number(classIdParam);
    const studentId = Number(studentIdParam);
    const isCurrentYear = !yearParam;
    const year = isCurrentYear ? new Date().getFullYear() : Number(yearParam);
    const {hasTab, hasFeature } = useSettings();
    
    // Assessment sub-tab state - initialize from URL params, then manage locally
    const assessmentTabParam = searchParams.get('assessmenttab');
    const overdueOnlyParam = searchParams.get('overdueonly');
    const initialAssessmentTab = assessmentTabParam ? Math.max(0, Math.min(2, parseInt(assessmentTabParam, 10))) : 0;
    const initialOverdueOnly = overdueOnlyParam === '1';
    
    const [assessmentMenuAnchor, setAssessmentMenuAnchor] = useState<null | HTMLElement>(null);
    const [assessmentSubTab, setAssessmentSubTab] = useState(initialAssessmentTab);
    const [onlyPublishedResults, setOnlyPublishedResults] = useState(false);
    const [overdueOnly, setOverdueOnly] = useState(initialOverdueOnly);
    
    const currentTab = searchParams.get('tab') || 'details';

    const { data } = new ApiQueries.student.ClassesByYear({ studentId, year, current: isCurrentYear }).ToQuery();
    const classData = data?.find(c => c.ClassID === classId);

    const { data: courseData } = new ApiQueries.courses.CourseByClassAndYear({ classId, year }).ToQuery();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        setSearchParams({ tab: newValue }, { replace: true });
    };
    
    const handleBackButtonClick = () => {
        navigate(-1);
    };

    const handleTeacherMessageClick = (contactId: number) => {
        navigate(`/message/new/10?contactid=${contactId}`);
    };

    const handleAssessmentTaskClick = (taskId: number, studentId: number) => {
        navigate(`/student/${studentId}/assessment/${taskId}`);
    };

    const showOutlineTab = hasTab(ShowTabsEnum.CourseOutline);
    const showAssessmentTab = hasTab(ShowTabsEnum.Assessment) && hasFeature(Features.AssessmentPlus);
    
    const handleAssessmentMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAssessmentMenuAnchor(event.currentTarget);
    };
    
    const handleAssessmentMenuClose = () => {
        setAssessmentMenuAnchor(null);
    };
    
    const handleAssessmentSubTabChange = (subTab: number) => {
        setAssessmentSubTab(subTab);
        handleAssessmentMenuClose();
    };
    
    const handleOverdueAssessmentClick = () => {
        // Switch to assessment tab
        setSearchParams({ tab: 'assessment' }, { replace: true });
        
        // Set to Tasks sub-tab
        setAssessmentSubTab(1);
        
        // Enable overdue filter
        setOverdueOnly(true);
    };
    
    // Assessment sub-tab labels
    const assessmentSubTabLabels = ['Summary', 'Assessment Tasks', 'Schedule'];
    
    // Build tab map dynamically based on what's visible
    const tabs = [
        { name: 'details', label: 'Details', component: <ClassDetails classId={classId} studentId={studentId} year={year} current={isCurrentYear} onTeacherMessageClick={handleTeacherMessageClick} onOverdueAssessmentClick={handleOverdueAssessmentClick} /> },
        ...(showOutlineTab ? [{ name: 'outline', label: 'Outline', component: <ClassOutline classId={classId} year={year} /> }] : []),
        { name: 'lessons', label: 'Lessons', component: <ClassLessons classId={classId} studentId={studentId} year={year} onLessonClick={handleItemClick} modalElement={modalElement} /> },
        { name: 'attendance', label: 'Attendance', component: <ClassAttendance classId={classId} studentId={studentId} year={year} current={isCurrentYear} /> }
    ];

    // Validate parameters - redirect to not found if invalid
    if (isNaN(classId) || isNaN(studentId) || isNaN(year)) {
        navigate('/notfound', { replace: true });
        return null;
    }
    

    return <>
        <PageHeader onBackButtonClick={handleBackButtonClick}>
            <Stack alignItems="center" gap={.5}>
                <span>{courseData?.Name || 'Class Details'}</span>
            </Stack> 
        </PageHeader>
        <PageContainer>
            {classData && courseData && <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="scrollable" value={currentTab} onChange={handleTabChange} aria-label="class tabs">
                        {tabs.map((tab) => (
                            <Tab key={tab.name} value={tab.name} label={tab.label} />
                        ))}
                        {showAssessmentTab && (
                            <Tab 
                                value="assessment" 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Assessment {assessmentSubTabLabels[assessmentSubTab]}
                                        <ExpandMore fontSize="small" />
                                    </Box>
                                }
                                onClick={handleAssessmentMenuClick}
                            />
                        )}
                    </Tabs>
                    <Menu
                        anchorEl={assessmentMenuAnchor}
                        open={Boolean(assessmentMenuAnchor)}
                        onClose={handleAssessmentMenuClose}
                    >
                        <MenuItem onClick={() => handleAssessmentSubTabChange(0)}>
                            Summary
                        </MenuItem>
                        <MenuItem onClick={() => handleAssessmentSubTabChange(1)}>
                            Tasks
                        </MenuItem>
                        <MenuItem onClick={() => handleAssessmentSubTabChange(2)}>
                            Schedule
                        </MenuItem>
                    </Menu>
                </Box>
                <Box sx={{ pt: 3 }}>
                    {currentTab === 'assessment' ? (
                        <AssessmentContent 
                            classId={classId}
                            studentId={studentId}
                            year={year}
                            isCurrentYear={isCurrentYear}
                            classData={classData}
                            courseData={courseData}
                            assessmentSubTab={assessmentSubTab}
                            onlyPublishedResults={onlyPublishedResults}
                            setOnlyPublishedResults={setOnlyPublishedResults}
                            overdueOnly={overdueOnly}
                            setOverdueOnly={setOverdueOnly}
                            onAssessmentTaskClick={handleAssessmentTaskClick}
                        />
                    ) : (
                        tabs.find(tab => tab.name === currentTab)?.component
                    )}
                </Box>
            </>}
        </PageContainer>
    </>
}

interface AssessmentContentProps {
    classId: number;
    studentId: number;
    year: number;
    isCurrentYear: boolean;
    classData: ApiQueries.student.StudentClass;
    courseData: ApiQueries.courses.Course;
    assessmentSubTab: number;
    onlyPublishedResults: boolean;
    setOnlyPublishedResults: (value: boolean) => void;
    overdueOnly: boolean;
    setOverdueOnly: (value: boolean) => void;
    onAssessmentTaskClick: (taskId: number, studentId: number) => void;
}

function AssessmentContent({
    studentId,
    classData,
    courseData,
    assessmentSubTab,
    onlyPublishedResults,
    setOnlyPublishedResults,
    overdueOnly,
    setOverdueOnly,
    onAssessmentTaskClick
}: AssessmentContentProps) {
    if (!classData || !courseData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    const classCode = classData.ClassCode;
    const dateRange = ApiQueries.assessment.AssessmentTaskListDateRange.Course;

    const handleTaskClick = (taskId: number) => {
        onAssessmentTaskClick(taskId, studentId);
    };

    return (
        <Box>
            {assessmentSubTab === 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                            size="small"
                            checked={onlyPublishedResults}
                            onChange={(e) => {
                                setOnlyPublishedResults(e.target.checked);
                                if (e.target.checked) {
                                    setOverdueOnly(false);
                                }
                            }}
                        />
                        <Typography>Only show published results</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                            size="small"
                            checked={overdueOnly}
                            onChange={(e) => {
                                setOverdueOnly(e.target.checked);
                                if (e.target.checked) {
                                    setOnlyPublishedResults(false);
                                }
                            }}
                        />
                        <Typography>Only show Overdue</Typography>
                    </Box>
                </Box>
            )}
            {assessmentSubTab === 0 && (
                <AssessmentSummary 
                    studentId={studentId} 
                    classCode={classCode} 
                    selectedDateRange={dateRange} 
                    onTaskClick={handleTaskClick}
                    courseId={courseData.CourseID}
                />
            )}
            {assessmentSubTab === 1 && (
                <AssessmentTaskList 
                    studentId={studentId} 
                    classCode={classCode} 
                    selectedDateRange={dateRange} 
                    onlyPublishedResults={onlyPublishedResults}
                    overdueOnly={overdueOnly}
                    onTaskClick={handleTaskClick}
                    courseId={courseData.CourseID}
                />
            )}
            {assessmentSubTab === 2 && (
                <AssessmentSchedule 
                    studentId={studentId} 
                    onTaskClick={handleTaskClick}
                    dateRange={dateRange}
                    classCode={classCode}
                    courseId={courseData.CourseID}
                />
            )}
        </Box>
    );
}
